import React, { ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import Text from './text';
import ProjectPermissionItem from '^/components/molecules/ProjectPermissionItem';
import { MediaQuery } from '^/constants/styles';
import { isRoleAdmin } from '^/utilities/role-permission-check';
import LoadingIcon from '^/components/atoms/LoadingIcon';
import { usePermissionStore } from '^/store/permissionStore';
import theme from '^/theme';
import EmptyState from '../EmptyState';
import { useL10n, UseL10n } from '^/hooks';
import TriStateCheckbox, { CheckboxState } from '^/components/atoms/TriStateCheckbox';
import { OpenContentPagePopup, OpenProjectPagePopup } from '^/store/duck/Pages';
import { usepatchPermissionMutation } from '^/store/react-query/permission';
import * as T from '^/types';
import {
  ContentPagePopupType,
  Language,
  Permission,
  PermissionRole,
  ProjectPagePopupType,
  State,
} from '^/types';

import ManageProjectBar from '../ManageProjectBar';
import { useGetPermissions } from '^/hooks/projects/useGetPermissions';
import { useUtilsStore } from '^/store/utilsStore';
import { UpdateConfirmDeletePermission } from '^/store/duck/Permissions';
import { usePopupStore } from '^/store/popup';
const Root = styled.table<{ isCompactSize: boolean }>(() => ({
  width: '100%',
}));

const TableHeaderRow = styled.tr({
  zIndex: 10,
  position: 'sticky',
  top: 2,
  borderTopWidth: '2px',
  boxShadow: `0 -2px 2px ${theme.colors.coolGray[300]}`,
  transform: `translateY(-1px)`,
  background: theme.colorTokens.bgLightCoolGray,
});

interface HeaderColumnProps {
  readonly columnMinWidth: number;
  readonly columnRatio: number;
}

const HeaderColumn = styled.th<HeaderColumnProps>(
  {
    paddingTop: '18px',
    paddingBottom: '12px',
    paddingLeft: '10px',
    [MediaQuery.MOBILE_L]: {
      display: 'none',
    },
  },
  ({ columnMinWidth, columnRatio }) => ({
    minWidth: `${columnMinWidth}px`,
    width: `${columnRatio}%`,
  })
);

const HeaderText = styled.span<{ isCompactSize: boolean }>(({ isCompactSize }) => ({
  display: 'inline-block',
  width: '100%',
  fontSize: isCompactSize ? '11.5px' : '13px',
  fontWeight: 700,
  color: theme.colors.neutralGray[500],
  textAlign: 'start',
  fontStyle: 'normal',
  lineHeight: '150%',
}));

const Body = styled.tbody({});
const Center = styled('div')({
  display: 'flex',
  height: '100px',
  justifyContent: 'center',
  alignItems: 'center',
});

const TableWrapper = styled.div({
  maxHeight: 'calc(100vh - 330px)',
  overflowY: 'scroll',
  width: '100%',
  marginTop: '20px',
});

const ProjectPermissionTable = ({
  isCompactSize,
  isContentPopup,
}: {
  isCompactSize: boolean;
  isContentPopup?: boolean;
}) => {
  const [l10n, language]: UseL10n = useL10n();
  const { selectedPermissions, setSelectedPermissions, setDeletePermission } = useUtilsStore(
    s => s
  );
  const searchText = usePermissionStore(s => s.searchText);
  const dispatch = useDispatch();
  const params = useParams();
  const currentProjectId = Number(params.id);
  const isInContent =
    isContentPopup ?? useSelector((state: State) => !state.Pages.Project.editingProjectId);
  const { Auth, Projects } = useSelector((state: State) => state);

  const setPopupOptions = usePopupStore(s => s.setPopupOptions);
  const setMinorPopup = usePopupStore(s => s.setMinorPopup);

  const { permissions: permissionRawData, isLoading: isPermissionLoading } = useGetPermissions({
    projectId: currentProjectId.toString(),
  });

  const permissionData = permissionRawData?.filter(permission => {
    const name =
      language === Language.KO_KR
        ? `${permission.lastName} ${permission.firstName}`
        : `${permission.firstName} ${permission.lastName}`;
    return (
      permission.email.toLowerCase().includes(searchText.toLocaleLowerCase()) ||
      name.toLowerCase().includes(searchText.toLocaleLowerCase())
    );
  });

  const { mutate: patchPermission } = usepatchPermissionMutation(currentProjectId);

  const selectedMemberIds = selectedPermissions.map(p => p.id);

  const authedId = Auth.authedUser?.id;
  const authedRole =
    Projects.projects.byId[currentProjectId]?.permissionRole || PermissionRole.VIEWER;
  const ownerId = Projects.projects.byId[currentProjectId]?.owner?.id;
  const featurePermission = Projects.projects.byId[currentProjectId]?.features || {
    ess: false,
    ddm: false,
    oneD: false,
    droneStation: false,
  };

  const handleDelete = (permission: Permission, hasPermission: boolean) => {
    if (!hasPermission) {
      if (isInContent) {
        dispatch(OpenContentPagePopup({ popup: ContentPagePopupType.NO_PERMISSION }));
      } else {
        dispatch(OpenProjectPagePopup({ popup: ProjectPagePopupType.NO_PERMISSION }));
      }
      return;
    }

    dispatch(UpdateConfirmDeletePermission({ permission }));
    setDeletePermission(permission);
    if (isInContent) {
      // we will update it later
      dispatch(OpenContentPagePopup({ popup: ContentPagePopupType.CONFIRM_DELETE_PERMISSION }));
    } else {
      // dispatch(OpenProjectPagePopup({ popup: ProjectPagePopupType.CONFIRM_DELETE_PERMISSION }));
      setPopupOptions({ manageProjectPopupType: T.ManageProjectPopupType.REMOVE_COLLABORATOR });
      setMinorPopup(T.ContentPageMinorPopupType.MANAGE_PROJECT_POPUP);
    }
  };

  const handlePermissionChange = (
    id: Permission['id'],
    role: PermissionRole,
    hasPermission: boolean
  ) => {
    if (!hasPermission) {
      if (isInContent) {
        dispatch(OpenContentPagePopup({ popup: ContentPagePopupType.NO_PERMISSION }));
      } else {
        dispatch(OpenProjectPagePopup({ popup: ProjectPagePopupType.NO_PERMISSION }));
      }
      return;
    }
    patchPermission({ role, id });
  };

  const handleFeaturePermissionChange = (
    id: Permission['id'],
    role: PermissionRole,
    isESS: boolean,
    hasPermission: boolean
  ) => {
    if (!hasPermission) {
      if (isInContent) {
        dispatch(OpenContentPagePopup({ popup: ContentPagePopupType.NO_PERMISSION }));
      } else {
        dispatch(OpenProjectPagePopup({ popup: ProjectPagePopupType.NO_PERMISSION }));
      }
      return;
    }
    patchPermission({ id, role, feature_ess: isESS });
  };

  const handleSelectMember = (permission: Permission, checked: boolean) => {
    if (checked) {
      setSelectedPermissions([...selectedPermissions, permission]);
    } else {
      setSelectedPermissions(selectedPermissions.filter((p: Permission) => p.id !== permission.id));
    }
  };

  const generateItem = (): ReactNode => {
    const result: Array<ReactElement<object>> = [];

    permissionData?.forEach(permission => {
      const hasPermission = isRoleAdmin(authedRole) && authedId !== permission.userId;
      const isOwner = ownerId === permission.userId;

      result.push(
        <ProjectPermissionItem
          zIndex={0}
          key={permission.userId}
          permission={permission}
          isOwner={isOwner}
          isCompactSize={isCompactSize}
          selectedMemberIds={selectedMemberIds}
          onSelectMember={handleSelectMember}
          featurePermission={featurePermission}
          isCurrentUser={authedId === permission.userId}
          onDelete={() => handleDelete(permission, hasPermission)}
          onPermissionChange={(id: Permission['id'], role: PermissionRole) =>
            handlePermissionChange(id, role, hasPermission)
          }
          onFeaturePermissionChange={(id: Permission['id'], role: PermissionRole, isESS: boolean) =>
            handleFeaturePermissionChange(id, role, isESS, hasPermission)
          }
        />
      );
    });
    return result;
  };

  const onSelectAllHandler = () => {
    if (selectedPermissions.length > 0) {
      setSelectedPermissions([]);
    } else {
      const filteredPermissions = permissionData?.filter(
        permission => permission.userId !== authedId && permission.userId !== ownerId
      );
      setSelectedPermissions(filteredPermissions || []);
    }
  };

  const selectAllState =
    selectedPermissions.length === permissionData?.length && selectedPermissions.length !== 0
      ? CheckboxState.CHECKED
      : selectedPermissions.length > 0
      ? CheckboxState.INDETERMINATE
      : CheckboxState.UNCHECKED;

  const loader = isPermissionLoading ? (
    <Center>
      <LoadingIcon />
    </Center>
  ) : null;

  const emptyPermission =
    permissionData?.length === 0 && !isPermissionLoading ? (
      <EmptyState
        showButton={false}
        title={`${l10n(Text.noResultsFound)} "${searchText}"`}
        subTitle={l10n(Text.noResultsFoundSubTitle)}
      />
    ) : null;

  return (
    <TableWrapper className="no-scrollbar">
      <Root isCompactSize={isCompactSize}>
        <thead>
          <TableHeaderRow>
            {!isCompactSize && (
              <HeaderColumn columnMinWidth={40} columnRatio={5}>
                <HeaderText isCompactSize={isCompactSize}>
                  <TriStateCheckbox state={selectAllState} onChange={onSelectAllHandler} />
                </HeaderText>
              </HeaderColumn>
            )}
            <HeaderColumn columnMinWidth={240} columnRatio={30}>
              <HeaderText isCompactSize={isCompactSize}>{l10n(Text.name)}</HeaderText>
            </HeaderColumn>
            <HeaderColumn columnMinWidth={160} columnRatio={15}>
              <HeaderText isCompactSize={isCompactSize}>{l10n(Text.organization)}</HeaderText>
            </HeaderColumn>
            <HeaderColumn columnMinWidth={160} columnRatio={20}>
              <HeaderText isCompactSize={isCompactSize}>{l10n(Text.role)}</HeaderText>
            </HeaderColumn>
            {featurePermission.ess && (
              <HeaderColumn columnMinWidth={160} columnRatio={10}>
                <HeaderText isCompactSize={isCompactSize}>
                  {l10n(Text.featureToggle.ess)}
                </HeaderText>
              </HeaderColumn>
            )}
            <HeaderColumn columnMinWidth={60} columnRatio={10}>
              <HeaderText isCompactSize={isCompactSize}>{l10n(Text.action)}</HeaderText>
            </HeaderColumn>
          </TableHeaderRow>
        </thead>
        <Body>{generateItem()}</Body>
      </Root>
      {loader}
      {emptyPermission}

      {selectedPermissions.length > 1 && (
        <ManageProjectBar
          isEssEnabled={featurePermission.ess}
          onClose={() => {
            setSelectedPermissions([]);
          }}
          selectedCount={selectedPermissions.length}
        />
      )}
    </TableWrapper>
  );
};

export default ProjectPermissionTable;
